// frontend/src/pages/ProductsPage.tsx
import { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  ArrowRight,
  Briefcase,
  BarChart2,
  Brain,
  ShoppingBag,
  Loader2,
  AlertTriangle,
  type LucideProps,
} from "lucide-react";

import { type ProductListed } from '@/types';
import { getProducts } from '@/api/productService';
import { useSiteSettingsStore } from '@/store/settingsStore'; // Import settings store

const getProductIcon = (slug: string): React.FC<LucideProps> => {
  if (slug.includes('modeller')) return Briefcase;
  if (slug.includes('insight')) return Brain;
  if (slug.includes('strategy')) return BarChart2;
  return ShoppingBag;
};

const ProductsPage = () => {
  const [products, setProducts] = useState<ProductListed[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get settings for dynamic header and conditional rendering
  const publicSettings = useSiteSettingsStore(state => state.publicSettings);
  const isLoadingSettings = useSiteSettingsStore(state => state.isLoading);

  const pageTitle = isLoadingSettings 
    ? "Loading..." 
    : publicSettings?.products_page_header_title || "Our Products";
  const pageSubtitle = isLoadingSettings
    ? "Loading description..."
    : publicSettings?.products_page_header_subtitle || "Discover our suite of innovative financial tools and platforms, designed to empower your trading and investment decisions.";

  // Determine if the contact page link should be shown
  const contactPageEnabled = isLoadingSettings ? true : publicSettings?.page_contact_enabled ?? true;


  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

  const getFullImageUrl = (relativePath: string | null | undefined): string | undefined => {
    if (!relativePath) return undefined;
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
      return relativePath;
    }
    if (relativePath.startsWith('/static/')) { 
        return `${backendBaseUrl}${relativePath}`;
    }
    return `${backendBaseUrl}/static/${relativePath}`; 
  };


  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const fetchedProducts = await getProducts({ status: 'active' });
        setProducts(fetchedProducts);
      } catch (err) {
        console.error("Failed to fetch products:", err);
        setError("Could not load products at this time. Please try again later.");
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading Products...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center min-h-[60vh] flex flex-col justify-center items-center">
        <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-6" />
        <h2 className="text-2xl font-semibold text-destructive mb-2">Error Loading Products</h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button onClick={() => window.location.reload()} variant="outline">Try Again</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 text-foreground bg-background">
      <div className="mb-10 md:mb-16 text-center border-b border-border/60 pb-10 relative">
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2/3 md:w-1/3 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent"></div>
        <ShoppingBag className="h-16 w-16 text-primary mx-auto mb-4" />
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
          {/* Dynamic Page Title */}
          <span dangerouslySetInnerHTML={{ __html: pageTitle.replace(/Products/g, '<span class="text-gradient-maroon-to-gold">Products</span>') || "Our <span class=\"text-gradient-maroon-to-gold\">Products</span>" }} />
        </h1>
        <p className="mt-4 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
          {/* Dynamic Page Subtitle */}
          {pageSubtitle}
        </p>
      </div>

      {products.length > 0 ? (
        <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => {
            const Icon = getProductIcon(product.slug);
            const fullImageUrl = getFullImageUrl(product.image_url); 
            return (
              <Card
                key={product.id}
                className="group flex flex-col bg-card text-card-foreground overflow-hidden shadow-lg hover:shadow-xl
                                 dark:hover:shadow-primary/10 transition-all duration-300 ease-in-out transform hover:-translate-y-1.5 border border-border/50
                                 hover:border-primary/40 dark:border-border dark:hover:border-primary/30 relative"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent/90 via-primary/90 to-accent/90 scale-x-0 group-hover:scale-x-100 transition-transform duration-400 ease-out origin-center"></div>
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400
                                 bg-gradient-to-br from-primary/5 via-background to-accent/5 dark:from-primary/10 dark:via-card dark:to-accent/10 "
                ></div>
                <div className="relative z-10 flex flex-col flex-grow h-full p-1">
                  {fullImageUrl ? (
                    <div className="h-48 w-full overflow-hidden rounded-t-md">
                        <img
                            src={fullImageUrl} 
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => (e.currentTarget.style.display = 'none')}
                        />
                    </div>
                  ) : (
                     <div className="h-48 w-full bg-muted/30 dark:bg-muted/10 flex items-center justify-center rounded-t-md">
                          <Icon className="h-16 w-16 text-muted-foreground/50" />
                      </div>
                  )}
                  <CardHeader className="items-center text-center pt-6 pb-3">
                    <CardTitle className="text-xl lg:text-2xl text-center text-foreground group-hover:text-primary transition-colors duration-300">
                      {product.name}
                    </CardTitle>
                    <CardDescription className="text-center min-h-[5.2rem] text-sm text-muted-foreground pt-1 line-clamp-3">
                      {product.description || 'No description available.'}
                    </CardDescription>
                  </CardHeader>
                  <CardFooter className="bg-transparent mt-auto pt-4 pb-6 px-6 group-hover:bg-muted/20 dark:group-hover:bg-muted/10 transition-colors duration-300">
                    <Button
                      asChild
                      className="w-full !bg-gradient-to-r !from-primary !to-vedasMaroon-700 hover:!from-primary/90 hover:!to-vedasMaroon-700/90 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <Link to={`/products/${product.slug}`}>
                        Learn More <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
            <ShoppingBag className="h-20 w-20 text-muted-foreground/50 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-foreground mb-2">No Products Available</h3>
            <p className="text-muted-foreground">
                We are currently updating our product listings. Please check back soon!
            </p>
        </div>
      )}

      {/* Conditionally render the "Contact Us" section */}
      {contactPageEnabled && (
        <section className="mt-16 md:mt-24 text-center py-12 px-6 bg-gradient-to-r bg-muted-via-background-gradient rounded-xl shadow-inner">
          <h2 className="text-3xl font-semibold mb-4 text-gradient-maroon-to-gold">
            Have Questions or Need a Custom Solution?
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            Our team is ready to assist you in finding the perfect tools to meet
            your financial objectives.
          </p>
          <Button
            size="lg"
            asChild
            className="!bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <Link to="/contact">Contact Us</Link>
          </Button>
        </section>
      )}
    </div>
  );
};

export default ProductsPage;
